/**
 * Risks.jsx
 * Display risk warnings based on entity risks and indicator keys
 * Migration from front/src/components/Risks.js
 */
import React, { memo } from 'react';
import { Typography } from '@mui/material';
import { risksText } from '../config/settings';
import useTokenReplacement from '../hooks/ui/useTokenReplacement';
import { printTextSize } from '../utils/entityHelpers';
import { shouldDisplayRisk } from '../utils/risksMapping';

/**
 * Resolve the domain variant for a risk, falling back to 'default'
 * @param {Object} riskVariants - Object with domain keys (e.g. { default: {...}, 'Nouvelle-Calédonie': {...} })
 * @param {string|undefined} domaine - Entity domain
 * @returns {Object|undefined} The matching variant or undefined if not found
 */
const getVariant = (riskVariants, domaine) =>
  (domaine && riskVariants[domaine]) ?? riskVariants['default'];

/**
 * Component to display risk warnings based on entity data and indicator
 * @param {Object} props - Component props
 * @param {Object} props.entity - Entity data (commune/EPCI)
 * @param {string} props.entity.type - Entity type ('commune' or 'epci')
 * @param {Array} props.entity.risques - Array of risk identifiers (technical IDs)
 * @param {number} props.entity.icu - ICU (Urban Heat Island) value
 * @param {string} props.entity.domaine - Entity domain (e.g. 'Nouvelle-Calédonie')
 * @param {string} props.indicatorKey - Current indicator key (R1, R2, etc.)
 * @returns {React.ReactElement|null} Risk warning component or null
 */
const Risks = ({
  entity: {
    type,
    risques: risks = [],
    icu,
    domaine,
  } = {},
  indicatorKey,
}) => {
  const replaceTokens = useTokenReplacement({ icu });

  // Special case: R3 indicator with both coastal risks
  if (indicatorKey === 'R3' &&
      risks.includes('risqueCotier') &&
      risks.includes('reculTraitCote')) {
    const riskVariants = risksText.risqueCotierEtReculTraitCote;
    const variant = getVariant(riskVariants, domaine);
    const message = type === 'commune' ? variant.commune : variant.epci;

    return (
      <Typography sx={{ ...printTextSize, fontWeight: '600' }}>
        {replaceTokens(message)}
      </Typography>
    );
  }

  // Find which risk (if any) should be displayed for this indicator
  const applicableRisk = risks.find(riskId => shouldDisplayRisk(riskId, indicatorKey));

  if (!applicableRisk) {
    return null;
  }

  // Get the appropriate message based on risk type and entity domain
  const riskVariants = risksText[applicableRisk];

  if (!riskVariants) {
    // eslint-disable-next-line no-console
    console.warn(`Missing risk text configuration for: ${applicableRisk}`);
    return null;
  }

  const variant = getVariant(riskVariants, domaine);

  if (!variant) {
    // eslint-disable-next-line no-console
    console.warn(`Missing risk text variant for: ${applicableRisk} / ${domaine}`);
    return null;
  }

  const message = type === 'commune' ? variant.commune : variant.epci;

  return (
    <Typography sx={{ ...printTextSize, fontWeight: '600' }}>
      {replaceTokens(message)}
    </Typography>
  );
};

Risks.displayName = 'Risks';

export default memo(Risks);
